// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the drafted task panel's inputs and its server context. A draft (from
// a chat suggestion, the email summary or the message menu) says what the task
// is; the origin says what it was made from; the context, fetched when the
// panel opens, says where it should go and why, who is in the conversation and
// whether this source already has a task (KonvoTaskDraftContext on the server).

import {Endpoints} from '@app/features/app/constants/Endpoints';
import ChannelStore from '@app/features/channel/state/Channels';
import {getDMDisplayName} from '@app/features/channel/utils/ChannelUtils';
import type {KonvoMailAddress, KonvoMailMessage} from '@app/features/konvo_mail/state/KonvoMail';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';

/** What an assistant draft fills in. The same shape the server's suggestions carry. */
export interface TaskSuggestion {
	name: string;
	description: string;
	list_id: string | null;
	assignee_id: number | null;
	due_date_ms: number | null;
	priority: number | null;
	reason: string | null;
}

export type TaskDraftOriginKind = 'mail' | 'chat' | 'whatsapp' | 'teams' | 'note';

export interface TaskDraftPersonInput {
	name: string | null;
	email: string | null;
	role: 'sender' | 'recipient' | 'named';
}

/** What the task is made from: the row at the top of the panel, and the server's matching hints. */
export interface TaskDraftOrigin {
	kind: TaskDraftOriginKind;
	/** "Re: Driver Group Mapping", "#dev", "Nuvio/iCAM Dev". */
	title: string;
	/** Who it came from, for the row's photo and "Gary Wels · today 10:02". */
	fromName: string | null;
	fromEmail: string | null;
	at: number | null;
	people: Array<TaskDraftPersonInput>;
	/** A chat's name: a list named like it is a strong hint. */
	channelName: string | null;
	/** Scopes a created task teaches, besides the sender's domain (worked out on the server). */
	scopes: Array<string>;
	/** Keys the created task is recorded under, to warn before making it twice. */
	sourceKeys: Array<string>;
	/** Mail only: finds tasks made from this email before the panel kept records. */
	subject: string | null;
	/** A line pointing back at the source, added to the task's description. */
	link: string | null;
}

export type TaskListReason =
	| {kind: 'learned'; scope: string}
	| {kind: 'domain'; domain: string; company: string}
	| {kind: 'channel'; channel: string}
	| {kind: 'recent'};

export interface TaskListCandidate {
	id: string;
	name: string;
	path: string;
	space: string;
	reason: TaskListReason;
}

export interface TaskDraftPerson {
	member_id: number;
	name: string;
	email: string;
	role: TaskDraftPersonInput['role'];
	guest: boolean;
}

export interface TaskDraftExisting {
	id: string;
	name: string;
	url: string;
	list_id: string;
	list_name: string;
	created_at: number | null;
}

export interface TaskDraftContext {
	connected: boolean;
	list_id: string | null;
	candidates: Array<TaskListCandidate>;
	learn_scopes: Array<string>;
	people: Array<TaskDraftPerson>;
	me: number | null;
	existing: TaskDraftExisting | null;
}

export async function fetchTaskDraftContext(origin: TaskDraftOrigin | null): Promise<TaskDraftContext | null> {
	try {
		const response = await http.post<TaskDraftContext>(Endpoints.KONVO_CLICKUP_TASK_DRAFT_CONTEXT, {
			body: {
				people: origin?.people ?? [],
				channel_name: origin?.channelName ?? null,
				scopes: origin?.scopes ?? [],
				source_keys: origin?.sourceKeys ?? [],
				subject: origin?.subject ?? null,
			},
			timeoutMs: 20_000,
		});
		return response.body ?? null;
	} catch {
		return null;
	}
}

// ── Origins ──────────────────────────────────────────────────────────────────

function addresses(list: ReadonlyArray<KonvoMailAddress> | undefined, role: TaskDraftPersonInput['role']) {
	return (list ?? []).map((entry) => ({name: entry.name, email: entry.address, role}));
}

/** An email: the sender, everyone it went to, and its thread as the duplicate key. */
export function mailTaskOrigin(
	mail: Pick<
		KonvoMailMessage,
		'id' | 'subject' | 'from_name' | 'from_address' | 'to' | 'received_at' | 'web_link' | 'conversation_id'
	> & {cc?: ReadonlyArray<KonvoMailAddress>},
): TaskDraftOrigin {
	const at = mail.received_at ? Date.parse(mail.received_at) : Number.NaN;
	const sender = mail.from_name ?? mail.from_address;
	return {
		kind: 'mail',
		title: mail.subject.trim() || '(no subject)',
		fromName: mail.from_name,
		fromEmail: mail.from_address,
		at: Number.isFinite(at) ? at : null,
		people: [
			...(mail.from_address || mail.from_name
				? [{name: mail.from_name, email: mail.from_address, role: 'sender' as const}]
				: []),
			...addresses(mail.to, 'recipient'),
			...addresses(mail.cc, 'recipient'),
		],
		channelName: null,
		scopes: [],
		// Outlook ids change when a mail moves folders; the thread id does not.
		sourceKeys: mail.conversation_id ? [`mailthread:${mail.conversation_id}`] : [`mail:${mail.id}`],
		subject: mail.subject,
		link: [`Email: ${mail.subject}${sender ? ` (from ${sender})` : ''}`, mail.web_link].filter(Boolean).join('\n'),
	};
}

/** A Konvo message: its chat's name, its author and the people it mentions. */
export function messageTaskOrigin(message: Message): TaskDraftOrigin {
	const channel = ChannelStore.getChannel(message.channelId);
	const channelName = channel ? (channel.name ?? getDMDisplayName(channel)) : null;
	const author = message.author.globalName ?? message.author.username;
	return {
		kind: 'chat',
		title: channel?.name ? `#${channel.name}` : (channelName ?? author),
		fromName: author,
		fromEmail: message.author.email ?? null,
		at: message.timestamp.getTime(),
		people: [
			{name: author, email: message.author.email ?? null, role: 'sender'},
			...message.mentions.map((user) => ({
				name: user.globalName ?? user.username,
				email: user.email ?? null,
				role: 'named' as const,
			})),
		],
		channelName,
		scopes: [`channel:${message.channelId}`],
		sourceKeys: [`message:${message.channelId}:${message.id}`],
		subject: null,
		link: null,
	};
}

/** A chat opened from Home (Konvo, WhatsApp, Teams): its name is the hint, its scope the learning key. */
export function chatTaskOrigin(params: {
	kind: 'whatsapp' | 'teams' | 'chat';
	/** "channel:<id>" for a Konvo chat (as the message menu uses), "chat:<jid>" for WhatsApp. */
	scope: string;
	name: string;
	at?: number | null;
}): TaskDraftOrigin {
	return {
		kind: params.kind,
		title: params.name,
		fromName: params.name,
		fromEmail: null,
		at: params.at ?? null,
		people: [{name: params.name, email: null, role: 'sender'}],
		channelName: params.name,
		scopes: [params.scope],
		sourceKeys: [],
		subject: null,
		link: null,
	};
}

// ── Small preferences ────────────────────────────────────────────────────────

const WHY_OPEN_KEY = 'konvo:task-draft:why-open';

/** Whether "Why Konvo picked these" starts open; it is closed until the user opens it once. */
export function readWhyOpen(): boolean {
	try {
		return getProtectedLocalStorage()?.getItem(WHY_OPEN_KEY) === '1';
	} catch {
		return false;
	}
}

export function writeWhyOpen(open: boolean): void {
	try {
		getProtectedLocalStorage()?.setItem(WHY_OPEN_KEY, open ? '1' : '0');
	} catch {
		// Private mode: the box just starts closed next time.
	}
}

/** The domain a scope teaches ("domain:icamvideo.co.za" -> "icamvideo.co.za"), for the learning note. */
export function scopeDomain(scope: string | undefined): string | null {
	return scope?.startsWith('domain:') ? scope.slice('domain:'.length) : null;
}
